// Gift Tier Manager
// - Shows forms for all reached tiers (2 & 3) based on window.progressBarSteps thresholds
// - Hides a tier if its gift product is already in the cart
// - Adds line item property: Gift: "<product title>" when adding

// Gift Tier Manager (resilient init)
// - Initializes when the container exists (even if inserted later)
// - Re-inits after ajax cart renders
// - Updates UI on cart updates and add-to-cart success

(() => {
    /*============================================================================
      Helper for rewards. Checks the amount spent and compare it to the rewards established by the element's dataset
    ==============================================================================*/
    // Build steps from the .tp-track arrows inside each .tier-progress block.
    // Each step has: { for_checkp, thresholdCents, reward }
    function buildTierStepsFromTrack(root) {
        const arrows = root.querySelectorAll('.tp-track .arrow');
        const steps = [];

        arrows.forEach(el => {
            const ds = el.dataset; // e.g. data-checkp-1="99", data-reward-1="Free Shipping"
            let idx = null;
            let dollars;
            let thresholdCents = null;
            let reward = '';

            // If Intelligems session variable exists and ds is Free Shipping
            const igFreeShippingThreshold = sessionStorage.getItem('freeShippingTest');
            if (igFreeShippingThreshold && ds['reward-1'] === 'Free Shipping') {
                idx = 1;
                thresholdCents = Math.round(Number(igFreeShippingThreshold));
                reward = 'Free Shipping';
            } else {
                Object.keys(ds).forEach(k => {
                    const matchCheckp = k.match(/^checkp-(\d+)$/);
                    if (matchCheckp) {
                        idx = Number(matchCheckp[1]);
                        dollars = parseFloat(ds[k]);
                        thresholdCents = Math.round(dollars * 100);
                        return;
                    }
                    const matchReward = k.match(/^reward-(\d+)$/);
                    if (matchReward) {
                        reward = ds[k];
                        return;
                    }
                });
            }

            // Update label content
            if (typeof Shopify?.formatMoney === 'function' && Number.isFinite(thresholdCents)) {
                el.innerText = Shopify.formatMoney(thresholdCents);
            }

            steps.push({
                for_checkp: idx,
                thresholdCents,
                reward,
            });
        });

        steps.sort((a, b) => a.thresholdCents - b.thresholdCents);
        return steps;
    }

    // Toggle rewards/heading based on current cart total
    function updateTierProgress(cart) {
        const totalCents = Number(cart?.total_price || 0);
        const root = document.querySelector('.tier-progress');
        if (!root) return;

        // Determine direction BEFORE changing the CSS var
        const prevSpent = parseFloat(getComputedStyle(root).getPropertyValue('--money-spent')) || 0;
        const newSpent = totalCents / 100;
        const isDecreasing = newSpent < prevSpent - 0.001;
        root.classList.toggle('is-decreasing', isDecreasing);

        // Ensure the new transition-delay rules apply BEFORE we change --money-spent
        void root.offsetWidth; // force reflow

        // Now update the progress fill (this triggers the animation)
        root.style.setProperty('--money-spent', newSpent.toFixed(2));

        // Build steps from the .tp-track arrows inside each .tier-progress block
        const steps = buildTierStepsFromTrack(root);
        window.progressBarSteps = steps;
        if (!steps.length) return;

        const grantedSteps = steps.filter(step => totalCents >= step.thresholdCents);
        const highestGrantedStep = grantedSteps.reduce((max, step) => {
            return (!max || step.for_checkp > max.for_checkp) ? step : max;
        }, null);

        steps.forEach(step => {
            const granted = totalCents >= step.thresholdCents;
            const idx = step.for_checkp;
            const rewardEl = root.querySelector(`[data-for-checkp="${idx}"]`);

            if (rewardEl) {
                rewardEl.classList.toggle('off', !granted);
            }

            // Update the heading: "Add $X to get <next reward>"
            const heading = root.querySelector('.tp-heading');
            const next = steps.find(s => totalCents < s.thresholdCents);
            if (heading) {
                if (next) {
                    const diff = next.thresholdCents - totalCents;
                    let formatted = typeof Shopify?.formatMoney === 'function' ?
                        Shopify.formatMoney(diff, window.settings?.moneyFormat) :
                        `$${(diff / 100).toFixed(2)}`;
                    formatted = formatted.replace(/\.00$/, '');
                    heading.textContent = `Add ${formatted} to get ${next.reward}`;
                } else {
                    heading.textContent = `You’ve unlocked all rewards!`;
                }
            }
        });

        // Sync cart UI label for Free Shipping state
        try {
            const fsEl = document.querySelector('#CartContainer p.free-shipping');
            if (fsEl) {
                const hasFreeShipping = grantedSteps.some(s => String(s.reward).trim() === 'Free Shipping');
                fsEl.innerText = hasFreeShipping ? 'FREE' : '';
            }
        } catch (e) {
            // no-op: defensive guard
        }
    }

    class GrantedTierGifts {
        constructor(container) {
            this.container = container;
            this.blocks = [];
            this.liveRegion = null;
        }

        getSteps() {
            const steps = Array.isArray(window.progressBarSteps) ? window.progressBarSteps : [];
            return steps
                .filter(s => typeof s?.thresholdCents === 'number' && typeof s?.for_checkp === 'number')
                .sort((a, b) => a.thresholdCents - b.thresholdCents);
        }

        computeReachedTier(cart) {
            const subtotal = Number(cart?.items_subtotal_price || 0);
            let reached = 0;
            for (const s of this.getSteps()) {
                if (subtotal >= s.thresholdCents) reached = Math.max(reached, Number(s.for_checkp));
            }
            return reached;
        }

        findBlocks() {
            this.blocks = Array.from(this.container.querySelectorAll('.granted-tier-gift[data-tier][data-product-id]'));
        }

        isGiftInCart(cart, productId) {
            const pid = Number(productId);
            return (
                Array.isArray(cart?.items) &&
                cart.items.some(i => Number(i.product_id) === pid && Number(i.final_line_price) === 0)
            );
        }

        updateUI(cart) {
            const reachedTier = this.computeReachedTier(cart);
            this.blocks.forEach(block => {
                const tier = Number(block.dataset.tier);
                const productId = Number(block.dataset.productId);
                const isReached = tier >= 2 && tier <= reachedTier;
                const alreadyInCart = this.isGiftInCart(cart, productId);
                console.log('[GTG] tier', tier, {
                    isReached,
                    alreadyInCart
                });
                block.classList.toggle('granted', isReached && !alreadyInCart);
            });
        }

        async fetchCart() {
            const res = await fetch('/cart.js', {
                headers: {
                    'Accept': 'application/json'
                }
            });
            if (!res.ok) throw new Error('Failed to load cart');
            return res.json();
        }

        initLiveRegion() {
            const region = document.createElement('div');
            region.setAttribute('aria-live', 'polite');
            region.setAttribute('aria-atomic', 'true');
            region.className = 'visually-hidden';
            this.container.appendChild(region);
            this.liveRegion = region;
        }

        bindEvents() {
            // Update when someone tells us the cart changed
            document.addEventListener('cart:updated', (e) => {
                const cart = e?.detail?.cart;
                if (cart) this.updateUI(cart);
                updateTierProgress(cart);
            });

            // Delegated click for free gift buttons
            this.container.addEventListener('click', async (e) => {
                const btn = e.target.closest('.free-atc-btn');
                if (!btn) return;

                const block = btn.closest('.granted-tier-gift');
                if (!block) return;

                const select = block.querySelector('.size-selector');
                const variantId = select?.value;
                const qty = Number(btn.dataset.defaultQty || 1) || 1;
                const productTitle = block.dataset.productTitle || 'Gift';

                if (!variantId) return;

                try {
                    btn.disabled = true;
                    btn.setAttribute('aria-busy', 'true');

                    const body = new URLSearchParams();
                    body.set('id', variantId);
                    body.set('quantity', String(qty));
                    body.set('properties[Gift]', productTitle);

                    const group = window.igData?.user.getTestGroup('285f3b86-0f0f-45ca-8721-025eb08e82e9');
                    const groupId = group?.id ?? null;

                    if (groupId) {
                    // Do something only if groupId is not null
                        body.set('properties[_igTestGroup]', groupId);
                    }

                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
                        },
                        body
                    });

                    if (!res.ok) {
                        const txt = await res.text();
                        throw new Error(`Add failed: ${res.status} ${txt}`);
                    }

                    // Refresh cart + notify listeners so cart UI re-renders
                    const cart = await this.fetchCart();
                    if (window.ajaxCart?.load) window.ajaxCart.load();
                    document.dispatchEvent(new CustomEvent('cart:refresh', {
                        detail: {
                            cart
                        }
                    }));

                    if (this.liveRegion) {
                        this.liveRegion.textContent = `${productTitle} added to cart as a free gift.`;
                    }
                } catch (err) {
                    console.error('[GTG] addGift error:', err);
                    if (this.liveRegion) {
                        this.liveRegion.textContent = 'Sorry, we could not add the gift. Please try again.';
                    }
                } finally {
                    btn.disabled = false;
                    btn.removeAttribute('aria-busy');
                }
            });
        }

        async init() {
            this.findBlocks();
            this.initLiveRegion();
            this.bindEvents();

            try {
                const cart = await this.fetchCart();
                updateTierProgress(cart);
                this.updateUI(cart);
            } catch (e) {
                console.warn('[GTG] initial cart fetch failed', e);
            }
        }
    }

    // Re-entrant initializer (safe to call multiple times)
    window.initGrantedTierGifts = async function initGrantedTierGifts(force) {
        const el = document.getElementById('GrantedTierGiftContainer');
        if (!el) {
            // nothing to do yet
            return;
        }
        if (el.dataset.gtgInit === '1' && !force) {
            return; // already initialized
        }
        el.dataset.gtgInit = '1';
        window.__GTG__ = new GrantedTierGifts(el);
        await window.__GTG__.init();
    };

    // Try once on ready if the container is already in DOM
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => window.initGrantedTierGifts());
    } else {
        window.initGrantedTierGifts();
    }

    // Common ajax-cart lifecycle hooks — call init after each render
    document.addEventListener('cart:rendered', () => window.initGrantedTierGifts(true));
    document.addEventListener('cart:refresh', (e) => {
      const cart = e?.detail?.cart;
      if (cart && window.__GTG__) {
        // Update in-place; no re-fetch or forced re-init needed
        try { window.__GTG__.updateUI(cart); } catch {}
        try { updateTierProgress(cart); } catch {}
      } else {
        // No cart provided: fall back to full re-init (which fetches)
        window.initGrantedTierGifts(true);
      }
    });
    if (window.jQuery) {
        // Many Timber/AjaxCart builds trigger this after render
        jQuery(document).on('ajaxCart.afterCartLoad', () => window.initGrantedTierGifts(true));
    }

    // Fallback: observe #CartContainer for template injection
    const observeTarget = document.getElementById('CartContainer') || document.body;
    const mo = new MutationObserver(() => window.initGrantedTierGifts());
    mo.observe(observeTarget, {
        childList: true,
        subtree: true
    });
})();


// // Debug: keep your existing console, plus show computed tiers
// document.addEventListener('cart:updated', (event) => {
//   const { cart } = event.detail || {};
//   console.log('hello from granted-tier-gift.liquid', cart);
//   console.log('progressBarSteps:', window.progressBarSteps);
// });
